import { Auth0AccountRole } from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import type { AutomationsApi } from "./api";
import { createBackendApi } from "./backendApi";
import { loadReference, ReferenceData, Viewer } from "./reference";
import type {
  ActivityFilter,
  ActivityQuery,
  ActivityRow,
  ApprovalFilter,
  ApprovalItem,
  Automation,
  ConnectedApps,
  UpcomingItem,
} from "./types";

/*
 * The Automations area's React side: AutomationsProvider loads Tadpole's
 * reference data and makes the API (over the backend); the hooks read
 * through it and read again after every change. Every provider in the
 * tab (the Automations page, a template's page, the Tasks pieces) shares
 * one load and one API per organization and signed-in person.
 */

type MakeApi = (ref: ReferenceData) => AutomationsApi;

/** One reference load per organization and viewer, shared in the tab. */
const loads = new Map<string, Promise<ReferenceData>>();
/** One API per reference and maker, shared in the tab. */
const apis = new WeakMap<ReferenceData, Map<MakeApi, AutomationsApi>>();

function loadShared(orgId: number, viewer: Viewer) {
  const key = `${orgId}|${viewer.tutor ? "tutor" : "admin"}|${
    viewer.referenceId ?? ""
  }`;
  let load = loads.get(key);
  if (!load) {
    load = loadReference(orgId, viewer);
    loads.set(key, load);
    load.catch(() => loads.delete(key));
  }
  return load;
}

function apiFor(ref: ReferenceData, makeApi: MakeApi) {
  let byMaker = apis.get(ref);
  if (!byMaker) {
    byMaker = new Map();
    apis.set(ref, byMaker);
  }
  let api = byMaker.get(makeApi);
  if (!api) {
    api = makeApi(ref);
    byMaker.set(makeApi, api);
  }
  return api;
}

/** The organization's shared data and API for this viewer. */
export function sharedAutomations(orgId: number, viewer: Viewer) {
  return loadShared(orgId, viewer).then((ref) => ({
    ref,
    api: apiFor(ref, createBackendApi),
  }));
}

/** Drops the organization's shared data, so the next load reads Tadpole again. */
function forget(orgId: number) {
  Array.from(loads.keys())
    .filter((key) => key.startsWith(`${orgId}|`))
    .forEach((key) => loads.delete(key));
}

/** Who is signed in, and whether they picked their tutor role. */
export function useViewer(): Viewer {
  const { account, currently_selected_role } = useContext(
    OrgRolesAndAccountContext
  );
  const referenceId = account?.reference_id ?? null;
  const tutor = currently_selected_role === Auth0AccountRole.ORG_TUTOR;
  return useMemo(() => ({ referenceId, tutor }), [referenceId, tutor]);
}

interface AutomationsContextValue {
  api: AutomationsApi;
  ref: ReferenceData;
}

const AutomationsContext = createContext<AutomationsContextValue | null>(null);

/**
 * Loads the organization's reference data, then provides the API. While
 * it loads it shows `loading`; if it can't, `failed` with the reason.
 * makeApi swaps in another AutomationsApi (a test's, say). fresh (the
 * Automations page): reads Tadpole again when it opens, so what was made
 * since (a template, a column, a form) shows; every provider and hook
 * after it shares that load.
 */
export function AutomationsProvider({
  orgId,
  children,
  loading = <LoadingRows />,
  failed = (reason: string) => (
    <p className="mt-6 text-sm text-red-600">{reason}</p>
  ),
  makeApi = createBackendApi,
  fresh = false,
}: {
  orgId: number;
  children: ReactNode;
  loading?: ReactNode;
  failed?: (reason: string) => ReactNode;
  makeApi?: MakeApi;
  fresh?: boolean;
}) {
  const viewer = useViewer();
  const [loaded, setLoaded] = useState<{ ref?: ReferenceData; error?: string }>(
    {}
  );
  const reread = useRef(fresh);

  useEffect(() => {
    let live = true;
    setLoaded({});
    if (reread.current) {
      reread.current = false;
      forget(orgId);
    }
    loadShared(orgId, viewer)
      .then((ref) => live && setLoaded({ ref }))
      .catch(
        (e) =>
          live &&
          setLoaded({
            error: errorText(
              e,
              e?.message || "Automations could not be loaded."
            ),
          })
      );
    return () => {
      live = false;
    };
  }, [orgId, viewer]);

  const api = useMemo(
    () => (loaded.ref ? apiFor(loaded.ref, makeApi) : null),
    [loaded.ref, makeApi]
  );
  const value = useMemo(
    () => (api && loaded.ref ? { api, ref: loaded.ref } : null),
    [api, loaded.ref]
  );

  if (loaded.error) return <>{failed(loaded.error)}</>;
  if (!value) return <>{loading}</>;
  return (
    <AutomationsContext.Provider value={value}>
      {children}
    </AutomationsContext.Provider>
  );
}

/**
 * The organization's shared data and API outside a provider (the Tasks
 * pieces), or null until they load or when they can't.
 */
export function useSharedAutomations(orgId: number | null | undefined) {
  const viewer = useViewer();
  const [shared, setShared] = useState<{
    ref: ReferenceData;
    api: AutomationsApi;
  } | null>(null);
  useEffect(() => {
    if (!orgId) {
      setShared(null);
      return;
    }
    let live = true;
    sharedAutomations(orgId, viewer)
      .then((found) => live && setShared(found))
      .catch(() => live && setShared(null));
    return () => {
      live = false;
    };
  }, [orgId, viewer]);
  return shared;
}

/** The app's loading look: two soft gray bars. */
export function LoadingRows() {
  return (
    <div className="mt-6 animate-pulse space-y-3" aria-label="Loading">
      <div className="h-10 rounded bg-gray-200" />
      <div className="h-10 rounded bg-gray-100" />
    </div>
  );
}

export function useAutomationsContext() {
  const value = useContext(AutomationsContext);
  if (!value)
    throw new Error("Automations components go inside AutomationsProvider.");
  return value;
}

/** The API, for changes: create, update, toggle, answer and the rest. */
export function useAutomationsApi() {
  return useAutomationsContext().api;
}

/** Tadpole's people, templates, kinds, boards, fields and tasks. */
export function useReference() {
  return useAutomationsContext().ref;
}

/**
 * Reads through the API now and after every change. key: what the read
 * depends on besides the API (an id, a filter), as text.
 */
function useApiRead<T>(read: (api: AutomationsApi) => Promise<T>, key = "") {
  const { api } = useAutomationsContext();
  const [state, setState] = useState<{
    data: T | undefined;
    error: string | null;
    loading: boolean;
  }>({
    data: undefined,
    error: null,
    loading: true,
  });
  const readRef = useRef(read);
  readRef.current = read;

  useEffect(() => {
    let live = true;
    let asked = 0;
    const load = () => {
      const mine = ++asked;
      readRef.current(api).then(
        (data) =>
          live &&
          mine === asked &&
          setState({ data, error: null, loading: false }),
        (e) =>
          live &&
          mine === asked &&
          setState((s) => ({
            ...s,
            error: errorText(e, "This could not be loaded."),
            loading: false,
          }))
      );
    };
    load();
    const unsubscribe = api.subscribe(load);
    return () => {
      live = false;
      unsubscribe();
    };
  }, [api, key]);

  return state;
}

/** Every automation, in the order they run. */
export function useAutomations() {
  const { data, error, loading } = useApiRead((api) => api.listAutomations());
  return { automations: data ?? ([] as Automation[]), error, loading };
}

/** One automation; null once it is deleted. */
export function useAutomation(id: string | null) {
  const { data, error, loading } = useApiRead(
    (api) => (id ? api.getAutomation(id) : Promise.resolve(null)),
    id ?? ""
  );
  return { automation: data ?? null, error, loading };
}

/** A name lookup for automations (for "Made by" filters), from the current list. */
export function useAutomationNames() {
  const { automations } = useAutomations();
  return useCallback(
    (id: string) => automations.find((a) => a.id === id)?.name ?? null,
    [automations]
  );
}

/** Waiting for approval, newest first: every item, or with mine, those the signed-in person can answer. */
export function useApprovals(filter: ApprovalFilter = {}) {
  const key = JSON.stringify(filter);
  const { data, error, loading } = useApiRead(
    (api) => api.listApprovals(JSON.parse(key)),
    key
  );
  return { approvals: data ?? ([] as ApprovalItem[]), error, loading };
}

/** Done: what automations did, newest first. */
export function useActivity(filter: ActivityFilter = {}) {
  const key = JSON.stringify(filter);
  const { data, error, loading } = useApiRead(
    (api) => api.listActivity(JSON.parse(key)),
    key
  );
  return { rows: data ?? ([] as ActivityRow[]), error, loading };
}

/**
 * One page of Activity's table. While another page, search or filter
 * loads, the one before stays (current is false until the new one is in).
 */
export function useActivityPage(query: ActivityQuery) {
  const key = JSON.stringify(query);
  const { data, error, loading } = useApiRead(
    (api) =>
      api.listActivityPage(JSON.parse(key)).then((found) => ({ key, found })),
    key
  );
  return {
    page: data?.found ?? null,
    current: data?.key === key,
    error,
    loading,
  };
}

/** Steps scheduled for later or waiting for an event. */
export function useUpcoming(automationId?: string) {
  const { data, error, loading } = useApiRead(
    (api) => api.listUpcoming(automationId),
    automationId ?? ""
  );
  return { upcoming: data ?? ([] as UpcomingItem[]), error, loading };
}

export function useConnectedApps() {
  const { data, error, loading } = useApiRead((api) => api.getConnectedApps());
  return { apps: data ?? null, error, loading } as {
    apps: ConnectedApps | null;
    error: string | null;
    loading: boolean;
  };
}
